import React from 'react'
import Head from 'next/head'
import StandardTubeRoute from '@src/routes/Configurators/products/Standard'
import StandardTubeRouteMTO from '@src/routes/Configurators/products/StandardMTO'
import TubesMTORedesign from '@src/routes/Configurators/products/TubesMTORedesign'
import getConfig from 'next/config'
import messages from '@src/routes/Configurators/messages'
import { useIntl } from 'react-intl'
import { useRouter } from '@src/routes'
import ConfiguratorPageError from '@src/routes/Configurators/utils/ConfiguratorPageError'
import DefaultLayout, {
  LayoutSpacingOptions,
} from '@src/components/DefaultLayout'
import { NextPage } from 'next'
import getCookiesFromContext from '@src/utils/getCookiesFromContext'
import { PRODUCT_DETAILS } from '@src/queries/ConfiguratorTubesQuery'
import { GetProductInformationQuery } from '@src/queries/ConfiguratorTubesQuery.generated'
import { isEmpty } from 'lodash'
import { CURRENT_USER } from '@src/queries/CurrentUserQuery'
import { CurrentUserQuery } from '@src/queries/CurrentUserQuery.generated'
import { getProductStatus } from '@src/routes/Configurators/utils/util'
import {
  checkForRedesignedProduct,
  useProductFromQueryParam,
} from '@src/routes/Configurators/products/TubesMTORedesign/ProductsConfiguration'
import { TubeProduct } from '@src/routes/Configurators/utils/enum'

const {
  publicRuntimeConfig: { featureFlags },
} = getConfig()

export interface StandardTubeProps {
  currentUser: CurrentUserQuery | null
  productDetails: GetProductInformationQuery | null
}

const updateProductAndFeatureFlags = (product) => {
  // This is so the old tube product is viewable when you pass
  // the param `product=standard-old` or other products in redesign
  // and the feature flag is enabled
  let isTubesRedesignFeatureFlagEnabled = featureFlags.tubesRedesign
  let isTubesRedesignRnaRnaEasyOligoPureAndSimple =
    featureFlags.tubesRedesignRnaEasyOligoPureAndSimple
  let isTubesRedesignIScaleLongWellRed =
    featureFlags.tubesRedesignIScaleLongWellRed
  let isTubesRedesignBeaconAndDualLabeled =
    featureFlags.tubesRedesignBeaconAndDualLabeled

  const productMapping = {
    [TubeProduct.StandardOld]: {
      newProduct: TubeProduct.Standard,
      featureFlagEnabled: isTubesRedesignFeatureFlagEnabled,
    },
    [TubeProduct.RnaOld]: {
      newProduct: TubeProduct.Rna,
      featureFlagEnabled: isTubesRedesignRnaRnaEasyOligoPureAndSimple,
    },
    [TubeProduct.EasyOligoOld]: {
      newProduct: TubeProduct.EasyOligo,
      featureFlagEnabled: isTubesRedesignRnaRnaEasyOligoPureAndSimple,
    },
    [TubeProduct.PureSimpleOld]: {
      newProduct: TubeProduct.PureSimple,
      featureFlagEnabled: isTubesRedesignRnaRnaEasyOligoPureAndSimple,
    },
    [TubeProduct.iscalednaOld]: {
      newProduct: TubeProduct.iscaledna,
      featureFlagEnabled: isTubesRedesignIScaleLongWellRed,
    },
    [TubeProduct.LongOligoOld]: {
      newProduct: TubeProduct.LongOligo,
      featureFlagEnabled: isTubesRedesignIScaleLongWellRed,
    },
    [TubeProduct.WellredOld]: {
      newProduct: TubeProduct.Wellred,
      featureFlagEnabled: isTubesRedesignIScaleLongWellRed,
    },
    [TubeProduct.BeaconOld]: {
      newProduct: TubeProduct.Beacon,
      featureFlagEnabled: isTubesRedesignBeaconAndDualLabeled,
    },
    [TubeProduct.ProbeOld]: {
      newProduct: TubeProduct.Probe,
      featureFlagEnabled: isTubesRedesignBeaconAndDualLabeled,
    },
  }

  if (productMapping[product]) {
    const { newProduct, featureFlagEnabled } = productMapping[product]
    if (featureFlagEnabled) {
      product = newProduct
      if (product === TubeProduct.Standard) {
        isTubesRedesignFeatureFlagEnabled = false
      } else {
        isTubesRedesignRnaRnaEasyOligoPureAndSimple = false
        isTubesRedesignIScaleLongWellRed = false
        isTubesRedesignBeaconAndDualLabeled = false
      }
    }
  }
  return {
    product,
    isTubesRedesignFeatureFlagEnabled,
    isTubesRedesignRnaRnaEasyOligoPureAndSimple,
    isTubesRedesignIScaleLongWellRed,
    isTubesRedesignBeaconAndDualLabeled,
  }
}

const StandardTube: NextPage<StandardTubeProps | null> = (initialData) => {
  const intl = useIntl()
  const router = useRouter()
  const initialProduct = useProductFromQueryParam()
  const countryCode = router?.query?.country || ''
  const {
    product,
    isTubesRedesignFeatureFlagEnabled,
    isTubesRedesignRnaRnaEasyOligoPureAndSimple,
    isTubesRedesignIScaleLongWellRed,
    isTubesRedesignBeaconAndDualLabeled,
  } = updateProductAndFeatureFlags(initialProduct)

  // "To order products, please contact your local dealer." is displayed
  // when this error is rendered
  if (
    //TODO: remove once the products gone line.
    //check is excluded for -old ones else the it will not be accesable
    !initialProduct.endsWith('-old') &&
    isEmpty(initialData?.productDetails?.getProductInformation?.productType)
  ) {
    const productCode = getProductStatus(
      initialData?.productDetails?.getProductInformation?.displayName || ''
    )

    return (
      <DefaultLayout spacing={LayoutSpacingOptions.MD}>
        <ConfiguratorPageError
          productCode={productCode}
          country={countryCode}
        />
      </DefaultLayout>
    )
  }

  let invalidProduct = false
  const productTitle = () => {
    let productKey = 'CONF_TITLE_' + product
    if (
      product === TubeProduct.Probe &&
      featureFlags.internalAndDualQuenchProbes
    ) {
      productKey = productKey + '_NEW'
    }
    let title = intl.formatMessage({
      id: 'CONF_ERROR_PAGE',
      defaultMessage: 'Error Page',
    })
    try {
      title = intl.formatMessage(messages[productKey])
    } catch (error) {
      invalidProduct = true
    }
    return title
  }

  const getTubeComponent = () => {
    if (
      featureFlags.internalAndDualQuenchProbes &&
      product === TubeProduct.Probe &&
      !isTubesRedesignBeaconAndDualLabeled
    ) {
      return (
        <StandardTubeRouteMTO
          currentUser={initialData?.currentUser || null}
          productDetails={initialData?.productDetails || null}
        />
      )
    } else if (
      (isTubesRedesignFeatureFlagEnabled ||
        isTubesRedesignRnaRnaEasyOligoPureAndSimple ||
        isTubesRedesignIScaleLongWellRed ||
        isTubesRedesignBeaconAndDualLabeled) &&
      checkForRedesignedProduct(
        product,
        isTubesRedesignFeatureFlagEnabled,
        isTubesRedesignRnaRnaEasyOligoPureAndSimple,
        isTubesRedesignIScaleLongWellRed,
        isTubesRedesignBeaconAndDualLabeled
      )
    ) {
      return <TubesMTORedesign />
    }
    return <StandardTubeRoute />
  }

  return (
    <React.Fragment>
      <Head>
        <title>{productTitle()}</title>
      </Head>
      {invalidProduct ? (
        <DefaultLayout spacing={LayoutSpacingOptions.MD}>
          <ConfiguratorPageError productCode={2} country={countryCode} />
        </DefaultLayout>
      ) : (
        getTubeComponent()
      )}
    </React.Fragment>
  )
}

StandardTube.getInitialProps = async (ctx: ExtendedNextPageContext) => {
  const cookies = getCookiesFromContext(ctx)
  const countryCookie = cookies.get('country') || 'US'
  const languageCookie = cookies.get('language') || 'en'
  const product = ctx?.query?.product
  let locale: string
  let isCsr: boolean
  let isCsrSupervisor: boolean
  let isGuestShoppingCountry: boolean
  let isOrderableCustomer: boolean
  let isOrderCustomer: boolean
  let sapNextEnabled: boolean
  let loggedIn
  locale = `${languageCookie}_${countryCookie}`
  isCsr = false
  isCsrSupervisor = false
  sapNextEnabled = true
  isOrderableCustomer = false
  try {
    let currentUserData: CurrentUserQuery | null = null
    const result = await ctx.apolloClient.query({
      query: CURRENT_USER,
    })
    currentUserData = result.data
    if (currentUserData?.me.__typename === 'LoggedInUser') {
      isCsr = currentUserData?.me.roles.isCustomerServiceRep
      isCsrSupervisor = currentUserData?.me.roles.isCustomProductCSR
      loggedIn = currentUserData?.me.__typename === 'LoggedInUser'
      locale = currentUserData?.me.metadata.locale
      sapNextEnabled = !!currentUserData.me.metadata.isSAPNextEnabled
      isGuestShoppingCountry = !!currentUserData.me.metadata.guestShoppingType
      isOrderCustomer =
        currentUserData.me.roles.isPublicOrderingCustomer ||
        currentUserData.me.roles.isQuoteRequester
      if (isGuestShoppingCountry !== null) {
        isOrderableCustomer = true
      } else if (isOrderCustomer && isGuestShoppingCountry === null) {
        isOrderableCustomer = true
      } else {
        isOrderableCustomer = false
      }
    }
    const inputData = {
      productType: product,
      countryCode: countryCookie,
      isCsr,
      isCsrSupervisor,
      isOrderableCustomer,
      regUser: loggedIn,
      locale,
      sapNextEnabled,
    }

    const productData = await ctx.apolloClient.query({
      query: PRODUCT_DETAILS,
      variables: { input: inputData },
    })

    return {
      currentUser: currentUserData,
      productDetails: productData?.data,
    }
  } catch (e) {
    console.error(e)
  }

  return null
}

export default StandardTube
